import { cloneDeep, toInteger } from 'lodash';
import { getFramework } from '@/framework';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas } from '@/utils/file-kit';
import { glKit } from '@/utils/gl-kit';
import { deleteExtraInfo } from '@/utils/layer-kit';
import { readSVGSize, svgToImage } from '@/utils/svg-kit';
import { transformTextDataToLowLever } from '@/utils/transform-kit';
import {
    type BoundingBox,
    type ILayerBehavior,
    type ILayerProps,
    type ITextContent,
    type ITextLayerProps,
} from '@/version-types';
import type { TextLayer } from '../domain';
import { EdgelessTextLayer } from '../edgeless';
import { createExportSVGResult } from '../edgeless/export';
import { ShapeTextRenderer } from '../edgeless/shape-text-renderer';
import { PPTSaver } from '../utils';

type Behaviors = Pick<ILayerBehavior, 'onBeforeSaveAsync' | 'savePPTLayerAsync' | 'getResultCanvasAsync'>;

/**
 * 创建 Text Layer 保存与输出行为。
 *
 * @param identity - 当前编辑器 framework 标识。
 * @returns 普通文字保存、出图与 PPT 输出行为。
 */
export const createSaveBehaviors = (identity: string) => {
    const behaviors: Behaviors = {
        /**
         * 保存前对文本图层数据的额外操作
         *
         * - 该方法会改变 mutable 入参
         *
         * @param layer - 图层属性
         * @param isAutoSave - 是否自动保存
         */
        async onBeforeSaveAsync(layer: ILayerProps, _isAutoSave: boolean) {
            const framework = getFramework(identity);

            /**
             * 将仍处于 GL 预览态的 Shape Text 替换为可保存的完整图层数据。
             *
             * @returns Shape Text 已准备完成或当前图层无需准备时结束；准备失败时抛出错误。
             */
            const prepareShapeText = async () => {
                const runtimeLayer = framework.service.flatLayer.getLayer(layer.id);
                if (!glKit.isUseGL(layer) && !(runtimeLayer && glKit.isUseGL(runtimeLayer))) return;

                const textLayer = framework.service.registration.getInstance<TextLayer>('TextLayer');
                const saveLayer = await textLayer?.refreshShapeTextRender(runtimeLayer || layer);
                if (!saveLayer) throw new Error(`Failed to prepare shape text for save: ${layer.id}`);

                Object.assign(layer, cloneDeep(saveLayer));
            };

            /**
             * 将 Shape Text 的本地截图上传为保存数据可使用的远程 URL。
             *
             * @param data - 当前待保存的 Text Layer 数据。
             * @returns 截图无需处理或上传完成时结束。
             */
            const uploadScreenshot = async (data: ITextLayerProps) => {
                if (!data.shapeExtension || !data.screenshotUrl) return;
                data.screenshotUrl = await base64Kit.getRemoteUrlAsync(data.screenshotUrl);
            };

            /**
             * 合并属性相同的相邻富文本分段，保留首段属性并拼接文本。
             *
             * @param contents - 已移除空项并与图层全文对齐的富文本分段。
             * @returns 合并后的富文本分段。
             */
            const mergeContents = (contents: ITextContent[]) => {
                const merged: ITextContent[] = [];

                for (const content of contents) {
                    const previous = merged[merged.length - 1];
                    const sameAttrs =
                        previous &&
                        JSON.stringify({ ...previous, text: '' }) === JSON.stringify({ ...content, text: '' });

                    if (sameAttrs) {
                        merged[merged.length - 1] = { ...previous, text: previous.text + content.text };
                    } else {
                        merged.push(content);
                    }
                }

                return merged;
            };

            /**
             * 将 Text Layer 数据整理为底层格式转换前的稳定保存形态。
             *
             * 先取整分段字号；普通富文本再移除空项、修正末段文本并合并相邻同属性分段。
             * 最终只剩一个分段时，将其共享样式提升到图层级并移除 textContents；花字保留逐字分段。
             *
             * @param data - 当前待保存的 Text Layer 数据。
             * @returns 不修改入参的保存形态 Text Layer 数据。
             */
            const normalizeData = (data: ITextLayerProps): ITextLayerProps => {
                const roundedContents = (data.textContents ?? []).map((content) => ({
                    ...content,
                    fontSize: toInteger(content.fontSize),
                }));
                const roundedData = roundedContents.length > 0 ? { ...data, textContents: roundedContents } : data;
                if (roundedData.advancedTextMaterial) return roundedData;

                let contents = cloneDeep(roundedContents.filter((content) => content.text !== ''));
                if (contents.length === 0) return roundedData;

                if (contents.map((content) => content.text).join('') !== roundedData.text) {
                    const lastIndex = contents.length - 1;
                    const prefixLength = contents
                        .slice(0, lastIndex)
                        .reduce((length, content) => length + content.text.length, 0);
                    contents[lastIndex] = {
                        ...contents[lastIndex],
                        text: roundedData.text.slice(prefixLength),
                    };
                }

                contents = mergeContents(contents);
                if (contents.length > 1) return { ...roundedData, textContents: contents };

                const [{ color, fontSize, fontFamily, bold, italic, lineThrough, underline, textMark }] = contents;
                const normalizedData: ITextLayerProps = {
                    ...roundedData,
                    color,
                    fontSize,
                    fontFamily,
                    bold,
                    italic,
                    lineThrough,
                    underline,
                };

                if (!roundedData.textMark && textMark) normalizedData.textMark = textMark;
                Reflect.deleteProperty(normalizedData, 'textContents');
                return normalizedData;
            };

            await prepareShapeText();

            deleteExtraInfo(layer);
            const layerData = layer.data as ITextLayerProps;
            await uploadScreenshot(layerData);
            layer.data = transformTextDataToLowLever(normalizeData(layerData));
        },

        /**
         * 文本图层出图
         *
         * @param layer - 文本图层
         * @param stageSize - 舞台尺寸
         * @returns 包含当前文字图层的舞台画布；输出不可用时返回 undefined。
         */
        async getResultCanvasAsync(layer, stageSize) {
            const object = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id);
            if (!(object instanceof EdgelessTextLayer)) return undefined;

            let source: HTMLCanvasElement | HTMLImageElement;
            let renderBox: BoundingBox;
            let applyLayerOpacity = false;

            if ((layer.data as ITextLayerProps).shapeExtension) {
                const framework = getFramework(identity);
                const renderer = framework.service.registration.getInstance<ShapeTextRenderer>('ShapeTextRenderer');
                if (!renderer) return undefined;
                ({ canvas: source, renderBox } = await renderer.getResult(layer));
                applyLayerOpacity = true;
            } else {
                const output = await object.resolveTextRenderOutputAsync(layer);

                const exportResult = createExportSVGResult(output, {
                    flipH: Boolean(layer.flipH),
                    flipV: Boolean(layer.flipV),
                    opacity: layer.opacity,
                    width: layer.width,
                    height: layer.height,
                });
                renderBox = exportResult.renderBox;

                const { width, height } = readSVGSize(exportResult.svg);
                if (width <= 0 || height <= 0) throw new Error('Text export failed: invalid export size.');

                // 文字导出边界通常带小数，例如 352.842 × 117.6。旧路径先画到向上取整的
                // 353 × 118 Canvas，再把整张位图缩回原尺寸贴到舞台，连取整补出的空白也参与缩放，
                // 已抗锯齿的字形边缘因此再次插值，造成文字发虚；小数位置和旋转会进一步放大差异。
                // 直接把 SVG image 按最终边界绘制到舞台，去掉中间位图和额外缩放，同时保留
                // 原有定位、旋转及 SVG 内的翻转/透明度；变形文字继续使用专用 renderer 的 Canvas。
                source = await svgToImage(new XMLSerializer().serializeToString(exportResult.svg));
            }

            const { canvas: stageCanvas, ctx } = createCanvas(stageSize.width, stageSize.height);

            ctx.translate(layer.left + layer.width / 2, layer.top + layer.height / 2);
            ctx.rotate((layer.rotate * Math.PI) / 180);
            if (applyLayerOpacity) ctx.globalAlpha = Math.max(0, layer.opacity);
            ctx.drawImage(
                source,
                renderBox.left - layer.width / 2,
                renderBox.top - layer.height / 2,
                renderBox.width,
                renderBox.height,
            );

            return stageCanvas;
        },

        /**
         * 将文本图层转为 ppt 图层写入 slide
         *
         * - 该方法会改变 mutable 入参
         *
         * @param slide - 幻灯片
         * @param layer - 图层属性
         * @param stageWidth - 舞台宽度
         * @param stageHeight - 舞台高度
         * @param watermark - 水印
         * @param outFontFamilyArr - 字体列表
         * @returns PPT 文字写入完成后的 Promise。
         */
        async savePPTLayerAsync(slide, layer, _stageWidth, _stageHeight, _watermark, outFontFamilyArr) {
            // PPT 保持可编辑的 layer-data 序列化，书写方向直接读取同一份持久化图层数据。
            const pptSaver = new PPTSaver(layer);
            pptSaver.save(slide, outFontFamilyArr);
        },
    };

    return behaviors;
};
